/**
 * Lane display constants — a DERIVED VIEW over the LANES registry, never a
 * hand-copy (Rule 3; the CP-R1312 vocabulary-fork class: a second
 * hand-enumerated lane table drifts from schema/lanes.json and ships the
 * drift as fact).
 *
 *   - The lane SET and ORDER derive from LANE_ORDER (the registry). A sixth
 *     lane, a missing lane, or a renamed id fails at registry load and at
 *     type-check — never silently here.
 *   - Fields the registry owns (badge label, accent color) are READ from the
 *     registry entry, never retyped.
 *   - Presentation-only fields with no registry source (long display name,
 *     emoji, route segment) live in LANE_PRESENTATION, a Record over the
 *     CLOSED LaneRegistryId union — TypeScript refuses a missing or extra key,
 *     so the presentational table cannot drift from the canon lane set.
 *   - Program flags derive from the lane id itself (sped is the IDEA lane,
 *     s504 the Section 504 lane, el the Title III lane) — structural facts of
 *     the five-lane canon, not copied booleans.
 */
import type { LaneId } from '../schemas/lanes'
import type { LaneRegistryId } from '../registries/lanes'
import { LANE_ORDER, getLane, isLaneRegistryId } from '../registries/lanes'

export interface LaneConstant {
  id: LaneId
  /** Long display name (presentation-only). */
  label: string
  /** Canonical badge label — read from the LANES registry. */
  shortLabel: string
  /** Accent color token — read from the LANES registry. */
  color: string
  emoji: string
  routeLabel: string
  isIDEA: boolean
  isSection504: boolean
  isTitleIII: boolean
}

/** Presentation-only values with no registry source, keyed by the closed lane union. */
const LANE_PRESENTATION: Record<LaneRegistryId, { label: string; emoji: string; routeLabel: string }> = {
  mtss: { label: 'MTSS', emoji: '🔄', routeLabel: 'mtss' },
  sped: { label: 'Special Education', emoji: '📘', routeLabel: 'special-education' },
  s504: { label: 'Section 504', emoji: '📗', routeLabel: 'section-504' },
  el: { label: 'English Learner', emoji: '🌐', routeLabel: 'english-learner' },
  health: { label: 'Health Plan', emoji: '🏥', routeLabel: 'health' },
}

export const LANE_DEFINITIONS: LaneConstant[] = LANE_ORDER.map((id) => {
  const entry = getLane(id)
  const presentation = LANE_PRESENTATION[id]
  return {
    id,
    label: presentation.label,
    shortLabel: entry.label,
    color: entry.accent,
    emoji: presentation.emoji,
    routeLabel: presentation.routeLabel,
    isIDEA: id === 'sped',
    isSection504: id === 's504',
    isTitleIII: id === 'el',
  }
})

export const LANE_MAP: Record<LaneId, LaneConstant> = Object.fromEntries(
  LANE_DEFINITIONS.map((l) => [l.id, l]),
) as Record<LaneId, LaneConstant>

export function isValidLane(id: string): id is LaneId {
  return isLaneRegistryId(id)
}
